import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Bell, BookOpen, Globe, Heart, PencilSimple, SignOut, Trash } from 'phosphor-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useFavoritesIds } from '@/hooks/useFavorites';
import { Monogram } from '@/components/ui/Monogram';
import { GoldenRule } from '@/components/ui/GoldenRule';
import { PageHeader } from '@/components/ui/PageHeader';
import { Segmented } from '@/components/ui/Segmented';
import { editionRoman, editionNumber } from '@/lib/edition';
import { applyTheme, getStoredTheme } from '@/lib/ambient';
import { BrandLogo } from '@/components/brand/BrandLogo';

function StatRow({ children }: { children: React.ReactNode }) {
  return <div className="t-mono-sm text-ink-2 tracking-wide">{children}</div>;
}

export default function Carnet() {
  const { user, profile, signOut } = useAuth();
  const fav = useFavoritesIds();
  const nav = useNavigate();
  const [cities, setCities] = useState(0);
  const [theme, setTheme] = useState<'light' | 'dark' | 'auto'>(getStoredTheme());

  useEffect(() => {
    (async () => {
      if (!user) return;
      if (fav.ids.size > 0) {
        const ids = Array.from(fav.ids);
        const { data } = await supabase.from('events').select('city').in('id', ids);
        const set = new Set((data ?? []).map((d: any) => d.city).filter(Boolean));
        setCities(set.size);
      }
    })();
  }, [user?.id, fav.ids.size]);

  function changeTheme(t: 'light' | 'dark' | 'auto') {
    setTheme(t); applyTheme(t);
  }

  const memberSince = user ? new Date(user.created_at).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '—';
  const name = profile?.display_name || user?.email?.split('@')[0] || 'Lecteur';

  return (
    <div className="bg-paper text-ink-1 min-h-screen pb-tabbar">
      <PageHeader eyebrow="Mon" title="Carnet" italicWord="Carnet" />

      {/* Identity card */}
      <section className="px-5 mb-8">
        <div className="bg-paper-elev ring-1 ring-paper-edge rounded-md p-4 flex items-center gap-4">
          <Monogram name={name} size={56} />
          <div className="flex-1 min-w-0">
            <div className="t-ed-lg truncate">{name}</div>
            <div className="t-mono-xs text-ink-3 mt-0.5">Membre depuis {memberSince}</div>
          </div>
          <button onClick={() => nav('/onboarding')} className="h-9 w-9 grid place-items-center rounded-full hover:bg-paper-edge"><PencilSimple size={16} /></button>
        </div>
      </section>

      {/* Stats */}
      <section className="px-5 mb-10">
        <div className="flex items-center gap-3 mb-3"><GoldenRule /><span className="t-cap text-ink-3 whitespace-nowrap">Statistiques</span><GoldenRule /></div>
        <StatRow>{fav.ids.size} sortie{fav.ids.size > 1 ? 's' : ''} · {cities} ville{cities > 1 ? 's' : ''}</StatRow>
      </section>

      {/* Sections */}
      <section className="px-5 space-y-1">
        <Row to="/agenda" icon={<Heart size={18} />} label="Mes favoris" hint={`${fav.ids.size} sorties`} />
        <Row to="/alerts" icon={<Bell size={18} />} label="Mes alertes" />
        <Row to="/vacation" icon={<Globe size={18} />} label="Choisir un lieu" hint={profile?.vacation_active ? `Actif · ${profile.vacation_label ?? ''}` : 'Inactif'} />
        <Row to="/mentions" icon={<BookOpen size={18} />} label="Mentions, données, sources" />
        <Row to="/legal" icon={<Trash size={18} />} label="Supprimer mon compte" />
      </section>

      {/* Theme */}
      <section className="px-5 mt-10">
        <div className="flex items-center gap-3 mb-3"><GoldenRule /><span className="t-cap text-ink-3 whitespace-nowrap">Préférences</span><GoldenRule /></div>
        <Segmented<'light' | 'dark' | 'auto'>
          value={theme}
          onChange={changeTheme}
          options={[{ id: 'light', label: 'Jour' }, { id: 'dark', label: 'Nuit' }, { id: 'auto', label: 'Auto' }]}
        />
      </section>

      <section className="px-5 mt-12">
        <button onClick={async () => { await signOut(); nav('/login'); }}
          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md ring-1 ring-paper-edge bg-paper-elev t-ui-sm text-ink-2">
          <SignOut size={16} /> <span className="font-serif italic">Se déconnecter</span>
        </button>
      </section>

      <footer className="px-5 mt-12 mb-6 flex flex-col items-center gap-2 text-center t-mono-xs text-ink-3">
        <BrandLogo size={28} className="text-ink-3" />
        <span>N° {editionRoman(editionNumber('carnet'))} · {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
}

function Row({ to, icon, label, hint }: { to: string; icon: React.ReactNode; label: string; hint?: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 py-3 border-b border-paper-edge last:border-0">
      <span className="text-ink-3">{icon}</span>
      <span className="flex-1 t-ui">{label}</span>
      {hint ? <span className="t-ui-xs text-ink-3">{hint}</span> : null}
      <ArrowUpRight size={14} className="text-ink-3" />
    </Link>
  );
}
